/**
 * HowToEditDialog.jsx — Create/edit a How To entry.
 *
 * URL-first flow: pasting a YouTube/Vimeo link auto-fills title + thumbnail
 * via the /api/oembed proxy. oEmbed failure degrades silently to manual
 * entry — it never blocks save. Fullscreen on mobile.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Autocomplete, Box, ToggleButton, ToggleButtonGroup,
  Tabs, Tab, Typography, IconButton, Stack, Alert, CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { api } from '../../lib/api';
import { parseVideoUrl } from '../../lib/videoUrl';
import { useMobile } from '../../hooks/useMobile';

const DEFAULT_CATEGORY = 'General';

function normalizeUrl(u) {
  const v = (u || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}

export default function HowToEditDialog({
  open,
  initial,          // null = new; {id,...} = edit; {url, kind} = new prefilled
  existingCategories = [],
  onClose,
  onSave,
  onDelete,
  canDelete = false,
  saving = false,
}) {
  const { isMobile } = useMobile();
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('howto');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [bodyTab, setBodyTab] = useState(0); // 0=Write 1=Preview
  const [error, setError] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [fetching, setFetching] = useState(false);
  const fetchedForUrl = useRef(null);

  useEffect(() => {
    if (!open) return;
    setUrl(initial?.url || '');
    setTitle(initial?.title || '');
    setKind(initial?.kind || 'howto');
    setCategory(initial?.category || '');
    setDescription(initial?.description || '');
    setBodyMd(initial?.bodyMd || '');
    setThumbnailUrl(initial?.thumbnailUrl || null);
    setBodyTab(0);
    setError('');
    fetchedForUrl.current = initial?.id ? (initial?.url || null) : null;
  }, [open, initial]);

  const isEdit = !!initial?.id;

  // ── oEmbed autofill: debounce on URL change, fill empty fields only ──
  useEffect(() => {
    if (!open) return;
    const normalized = normalizeUrl(url);
    if (!normalized || normalized === fetchedForUrl.current) return;
    // URL changed away from the last-fetched one → drop the stale thumbnail
    // immediately so it can never be saved against a different video/link.
    // (YouTube thumbs are re-derived in the card; only Vimeo/IG lose the preview.)
    setThumbnailUrl(null);
    if (!parseVideoUrl(normalized)) return;
    let stale = false;
    const timer = setTimeout(async () => {
      setFetching(true);
      try {
        const meta = await api.get(`/oembed?url=${encodeURIComponent(normalized)}`);
        if (stale) return; // a newer URL superseded this fetch
        fetchedForUrl.current = normalized;
        setThumbnailUrl(meta.thumbnailUrl || null);
        setTitle((t) => t.trim() ? t : (meta.title || ''));
      } catch {
        // Silent: unlisted/private videos or network issues → manual entry
        if (!stale) fetchedForUrl.current = normalized;
      } finally {
        setFetching(false);
      }
    }, 600);
    return () => { stale = true; clearTimeout(timer); };
  }, [url, open]);

  const categoryOptions = useMemo(() => {
    const set = new Set([DEFAULT_CATEGORY, ...existingCategories.filter(Boolean)]);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [existingCategories]);

  const handleSave = () => {
    setError('');
    const t = title.trim();
    const finalUrl = normalizeUrl(url);
    const finalBody = bodyMd.trim();
    if (!t) return setError('Title is required');
    if (!finalUrl && !finalBody) return setError('Add a link, some notes, or both');
    if (finalUrl) {
      try { new URL(finalUrl); } catch { return setError('Invalid URL'); }
    }
    onSave({
      ...(initial?.id ? { id: initial.id } : {}),
      title: t,
      kind,
      category: (category || '').trim() || DEFAULT_CATEGORY,
      description: description.trim() || null,
      url: finalUrl || null,
      bodyMd: finalBody || null,
      thumbnailUrl: finalUrl ? thumbnailUrl : null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {isEdit ? 'Edit Entry' : 'New Entry'}
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Video or link URL"
            placeholder="Paste a YouTube, Vimeo, or Instagram link…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoFocus={!isEdit}
            fullWidth
            InputProps={{
              endAdornment: fetching ? <CircularProgress size={16} /> : null,
            }}
            helperText="YouTube, Vimeo, and Instagram links auto-fill the title and play right on the page."
          />
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Section
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={kind}
                onChange={(_, v) => v && setKind(v)}
              >
                <ToggleButton value="howto" sx={{ textTransform: 'none' }}>How-To</ToggleButton>
                <ToggleButton value="inspiration" sx={{ textTransform: 'none' }}>Inspiration</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Autocomplete
              freeSolo
              options={categoryOptions}
              value={category}
              onChange={(_, v) => setCategory(v || '')}
              onInputChange={(_, v) => setCategory(v || '')}
              sx={{ flex: 1, minWidth: 200 }}
              renderInput={(params) => (
                <TextField {...params} label="Category" size="small" placeholder={DEFAULT_CATEGORY} />
              )}
            />
          </Box>
          <TextField
            label="Description"
            placeholder="Short blurb shown on the card and detail page"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />

          <Box>
            <Tabs value={bodyTab} onChange={(_, v) => setBodyTab(v)} sx={{ mb: 1, minHeight: 36 }}>
              <Tab label="Notes" sx={{ textTransform: 'none', minHeight: 36 }} />
              <Tab label="Preview" sx={{ textTransform: 'none', minHeight: 36 }} />
            </Tabs>
            {bodyTab === 0 ? (
              <TextField
                value={bodyMd}
                onChange={(e) => setBodyMd(e.target.value)}
                multiline
                minRows={6}
                fullWidth
                placeholder={'Markdown notes — key takeaways, settings, gear…'}
                sx={{ '& textarea': { fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: '0.85rem' } }}
              />
            ) : (
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, minHeight: 160, '& > *:first-of-type': { mt: 0 } }}>
                {bodyMd.trim()
                  ? <ReactMarkdown>{bodyMd}</ReactMarkdown>
                  : <Typography variant="body2" color="text.secondary">Nothing to preview yet.</Typography>}
              </Box>
            )}
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {isEdit && canDelete && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={onDelete} disabled={saving}>
              Delete
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ ml: 1 }}>
            {isEdit ? 'Save Changes' : 'Add Entry'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
